
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  isNotNullish,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  FieldValue,
  FormField,
  FormFieldCheckbox,
  FormFieldComboBox,
  FormFieldFileInput,
  FormFieldMultiSelect,
  FormFieldRichTextInput,
  FormFieldUrlAliasInput,
  FormFooter,
  OptionType,
  SeoChangeEvent,
  SeoFieldGroup,
  TabType,
  TagerFormSubmitEvent,
  useSelectOptionsResource,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";
import {
  BooleanFieldConfig,
  ColorFieldConfig,
  DynamicField,
  FieldShortType,
  FieldUnion,
  IncomingValueUnion,
  MultiSelectFieldConfig,
  NumberFieldConfig,
  RepeaterIncomingValue,
  SelectFieldConfig,
  StringFieldConfig,
  universalFieldUtils,
} from "@tager/admin-dynamic-field";

import {
  BrandType,
  CategoryType,
  FullProductType,
  FullProductTypeType,
  ProductGroupType,
  ProductTypeType,
  SearchType,
  SpecialType,
} from "@/typings/model";
import {
  createProduct,
  getBrandList,
  getCategoryList,
  getCollectionSearchList,
  getProduct,
  getProductGroupList,
  getProductType,
  getProductTypeList,
  getSpecialList,
  updateProduct,
} from "@/services/requests";
import {
  getBasisProductFormUrl,
  getProductFormUrl,
  getProductListUrl,
} from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";
import ExtendedPrice from "@/views/ProductForm/components/ExtendedPrice.vue";

import {
  availabilityOptionList,
  convertCategoryListToOption,
  convertFormValuesToProductCreationPayload,
  convertFormValuesToProductUpdatePayload,
  convertListToOption,
  convertProductToFormValues,
  convertProductTypeFieldListToFieldConfigUnion,
  convertSameProductToRepeaterIncomingValue,
  discountTypeOption,
  FormValues,
  productFieldConfig,
} from "./ProductForm.helpers";

export default defineComponent({
  name: "ProductForm",
  components: {
    Page,
    FormField,
    FormFieldCheckbox,
    FormFieldFileInput,
    FormFieldComboBox,
    FormFieldRichTextInput,
    FormFieldUrlAliasInput,
    DynamicField,
    ExtendedPrice,
    FormFooter,
    FieldValue,
    SeoFieldGroup,
    FormFieldMultiSelect,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const productId = computed<string>(() => route.params.productId as string);

    const isCreation = computed<boolean>(() => productId.value === "create");

    const [
      fetchProductTypeList,
      { data: productTypeList, loading: isProductTypeListLoading },
    ] = useResource<Array<ProductTypeType>>({
      fetchResource: getProductTypeList,
      initialValue: [],
      resourceName: "Product Type list",
    });

    const [
      fetchCategoryList,
      { data: categoryList, loading: isCategoryListLoading },
    ] = useResource<Array<CategoryType>>({
      fetchResource: () =>
        getCategoryList({
          pageNumber: 1,
          pageSize: 99999,
        }),
      initialValue: [],
      resourceName: "Category list",
    });

    const [fetchProduct, { data: product, loading: isProductLoading }] =
      useResource<Nullable<FullProductType>>({
        fetchResource: () => getProduct(productId.value),
        initialValue: null,
        resourceName: "product",
      });

    const {
      loading: isBrandListLoading,
      options: brandOptionList,
      fetchEntityList: fetchBrandList,
    } = useSelectOptionsResource<BrandType, OptionType<number>>({
      fetchEntityList: getBrandList,
      resourceName: "Brand list",
      minQueryLength: 0,
      convertEntityToOption: (brand) => {
        return {
          value: brand.id,
          label: brand.name,
        };
      },
    });

    const [
      fetchProductGroupList,
      { data: productGroupList, loading: isProductGroupListLoading },
    ] = useResource<Array<ProductGroupType>>({
      fetchResource: getProductGroupList,
      initialValue: [],
      resourceName: "Product group list",
    });

    const [
      fetchSpecialList,
      { data: specialList, loading: isSpecialListLoading },
    ] = useResource<Array<SpecialType>>({
      fetchResource: getSpecialList,
      initialValue: [],
      resourceName: "Special list",
    });

    const [
      fetchCollectionSearchList,
      { data: collectionSearchList, loading: isCollectionSearchListLoading },
    ] = useResource<Array<SearchType>>({
      fetchResource: () =>
        getCollectionSearchList({
          query: "",
          brand: product.value?.brand?.id ?? "",
        }),
      initialValue: [],
      resourceName: "Collection search list",
    });

    onMounted(() => {
      fetchCategoryList();
      fetchBrandList({ query: "" });
      if (isCreation.value) {
        fetchProductTypeList();
        return;
      }
      fetchProductGroupList();
      fetchSpecialList();
      fetchProduct();
    });

    watch(productId, () => {
      fetchCategoryList();
      fetchProductGroupList();
      fetchSpecialList();
      fetchProduct();
    });

    const productTypeOptionList = ref<Array<OptionType<number>>>(
      convertListToOption(productTypeList.value)
    );

    const categoryOptionList = ref<Array<OptionType<number>>>(
      convertCategoryListToOption(categoryList.value)
    );

    const productGroupListOption = ref<Array<OptionType<number>>>(
      convertListToOption(productGroupList.value)
    );

    const specialListOption = ref<Array<OptionType<number>>>(
      convertListToOption(specialList.value)
    );

    const collectionSearchListOption = ref<Array<OptionType<number>>>([]);

    const values = ref<FormValues>(
      convertProductToFormValues(
        product.value,
        categoryOptionList.value,
        brandOptionList.value,
        productGroupListOption.value,
        specialListOption.value
      )
    );

    function setValues() {
      values.value = convertProductToFormValues(
        product.value,
        categoryOptionList.value,
        brandOptionList.value,
        productGroupListOption.value,
        specialListOption.value
      );
    }

    const [
      fetchProductType,
      { data: productType, loading: isProductTypeLoading },
    ] = useResource<Nullable<FullProductTypeType>>({
      fetchResource: () => getProductType(values?.value?.productType?.value),
      initialValue: null,
      resourceName: "productType",
    });

    watch(
      () => values.value.productType,
      () => {
        if (values.value.productType) {
          fetchProductType();
        }
      }
    );

    watch(productType, () => {
      if (productType && productType.value) {
        const currentCategoryOption = categoryOptionList.value.find(
          (categoryOption) =>
            categoryOption.value === productType.value?.defaultCategoryId
        );
        if (values.value) {
          values.value.category = currentCategoryOption ?? null;
        }
      }
    });

    const inputsSameProductFieldIncomingValue = computed<RepeaterIncomingValue>(
      () => convertSameProductToRepeaterIncomingValue(values.value.sameProducts)
    );

    const inputsSameProductField = ref<FieldUnion>(
      universalFieldUtils.createFormField(
        productFieldConfig(),
        inputsSameProductFieldIncomingValue.value
      )
    );

    function updateInputCategoryTableValues() {
      inputsSameProductField.value = universalFieldUtils.createFormField(
        productFieldConfig(),
        inputsSameProductFieldIncomingValue.value
      );
    }

    const productTypeValues = ref<Array<FieldUnion>>([]);

    function updateProductTypeValues() {
      if (!product.value) return null;
      const fieldProductTypeList: Array<
        | SelectFieldConfig
        | MultiSelectFieldConfig
        | NumberFieldConfig
        | BooleanFieldConfig
        | ColorFieldConfig
        | StringFieldConfig
      > =
        convertProductTypeFieldListToFieldConfigUnion(
          product.value.fieldValues
        ) ?? [];

      const incomingFieldList: Array<FieldShortType<IncomingValueUnion>> =
        product.value?.fieldValues.map((fieldValue) => ({
          name: String(fieldValue.field.id),
          value: fieldValue.value,
        })) ?? [];

      productTypeValues.value = fieldProductTypeList.map(
        (fieldConfig, index) => {
          return universalFieldUtils.createFormField(
            fieldConfig,
            incomingFieldList[index]?.value
          );
        }
      );
    }

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(product, () => {
      setValues();
      fetchCollectionSearchList();
    });

    watch([productTypeList], () => {
      productTypeOptionList.value = convertListToOption(productTypeList.value);
    });

    watch(product, () => {
      updateProductTypeValues();
    });

    watch(inputsSameProductFieldIncomingValue, () => {
      updateInputCategoryTableValues();
    });

    watch(categoryList, () => {
      categoryOptionList.value = convertCategoryListToOption(
        categoryList.value
      );

      setValues();
    });

    watch(collectionSearchList, () => {
      collectionSearchListOption.value = convertListToOption(
        collectionSearchList.value
      );
      setValues();
    });

    watch(productGroupList, () => {
      productGroupListOption.value = convertListToOption(
        productGroupList.value
      );
      setValues();
    });

    watch(specialList, () => {
      specialListOption.value = convertListToOption(specialList.value);
      setValues();
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToProductCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToProductUpdatePayload(
        values.value,
        productTypeValues.value,
        inputsSameProductField.value
      );
      const requestPromise = isCreation.value
        ? createProduct(creationBody)
        : updateProduct(productId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getProductFormUrl({ productId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getProductListUrl());
          }
          if (event.type === "create_create-another") {
            setValues();
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Товар успешно добавлен`
              : "Товар успешно обновлен",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении товара`
              : "Ошибка при обновлении товара",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () =>
        isProductLoading.value ||
        isProductTypeListLoading.value ||
        isProductGroupListLoading.value ||
        isSpecialListLoading.value ||
        isCollectionSearchListLoading.value ||
        isBrandListLoading.value ||
        isCategoryListLoading.value
    );

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      return [
        {
          id: "common",
          label: "Основное",
          hasErrors: hasErrors([
            "title",
            "subtitle",
            "urlAlias",
            "image",
            "content",
          ]),
        },
        {
          id: "media",
          label: "Медиа",
          hasErrors: hasErrors([
            "gallery",
            "schemaImage",
            "youtubeVideo",
            "video",
          ]),
        },
        {
          id: "specifications",
          label: "Характеристики",
          hasErrors: hasErrors([]),
        },
        {
          id: "connections",
          label: "Связи",
          hasErrors: hasErrors(["category", "brand"]),
        },
        {
          id: "price",
          label: "Цена и наличие",
          hasErrors: hasErrors(["price", "discountType", "discountValue"]),
        },
        {
          id: "seo",
          label: "SEO",
          hasErrors: hasErrors([
            "pageTitle",
            "pageDescription",
            "openGraphImage",
          ]),
        },
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить товар`
        : product.value
        ? `Редактирование товара - "${product.value.title}${
            product.value.subtitle ? ` - ${product.value.subtitle}` : ""
          }" ${product.value.code ? `(код товара ${product.value.code})` : ""}`
        : "Загрузка товара…";
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/products/";
    });

    const headerButtonList = computed<
      Array<{ text: string; href: string; class?: string; target?: string }>
    >(() =>
      [
        product.value
          ? {
              text: "Просмотреть на сайте",
              href: getWebsiteOrigin() + "/products/" + product.value.urlAlias,
              target: "_blank",
            }
          : null,
      ].filter(isNotNullish)
    );

    function resetDiscountValue() {
      values.value.discountValue = "";
      document.getElementById("discountValue")?.focus();
    }

    function getDiscountPricePercent(price: number, discountValue: number) {
      return (price * (100 - discountValue)) / 100 >= 0
        ? (price * (100 - discountValue)) / 100
        : 0;
    }

    function getDiscountPriceValue(price: number, discountValue: number) {
      return price - discountValue >= 0 ? price - discountValue : 0;
    }

    function handleSearchEngineOptimization({
      title,
      description,
      image,
    }: SeoChangeEvent) {
      values.value.pageTitle = title;
      values.value.pageDescription = description;
      values.value.openGraphImage = image;
    }

    return {
      backButtonUrl: getProductListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      product,
      isCreation,
      pageTitle,
      getBasisProductFormUrl,
      tabList,
      selectedTabId,
      productTypeOptionList,
      categoryOptionList,
      productGroupListOption,
      specialListOption,
      discountTypeOption,
      pagePath,
      headerButtonList,
      productTypeValues,
      getDiscountPricePercent,
      getDiscountPriceValue,
      resetDiscountValue,
      collectionSearchListOption,
      inputsSameProductField,

      brandOptionList,

      availabilityOptionList,

      handleSearchEngineOptimization,
    };
  },
});
